// Sugalarm — Cycle 456: the IOB chip on the "Who's treating?" picker.
//
// ⚠ NO NEW IOB MATH. The chip shows exactly the entries the IOB card on the
// treating screen shows — the SAME `activeBolusesForRings` boluses (at most
// two, each already coloured meal/correction by NowScreen through
// `isCorrectionBolus`) and the SAME `basalForContext` long-acting dose — so it
// can never hold more than three entries, by construction (operator
// decision 3, 2026-09-30). The remaining fraction, the time left and the
// long-acting label come from the existing helpers and formatters
// (`remainingFraction`, `iobTimeRemainingMs`, `formatBasalHoursLeft`).
// The IOB card and its dual circular display are untouched.

import {
  CORRECTION_BOLUS_COLOR,
  formatBasalHoursLeft,
  iobTimeRemainingMs,
  remainingFraction,
  type BasalContext,
  type BolusForRings,
} from "./iob";

/** The treating screen's legend time — "2h 41m" / "41m" / "1m". Floors at
 *  1m so a drawn ring never reads 0m. (Moved here from `TreatingScreen` in
 *  cycle 456 so the chip and the legend share it.) */
export function coarseLeft(ms: number): string {
  const totalMin = Math.max(1, Math.ceil(ms / 60_000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export type IobChipKind = "meal" | "correction" | "long_acting";

export interface IobChipEntry {
  key: string;
  kind: IobChipKind;
  /** "2.5u · 1h 40m" — bold, beside the ring. */
  amount: string;
  /** "Meal" / "Correction" / "Long-acting" — the 13 px type label. */
  label: string;
  /** Remaining fraction in [0, 1] for the ring. */
  fraction: number;
  color: string;
}

/** Ring colours by TYPE (as mocked): orange meal, pink correction, purple
 *  long-acting. ⚠ The pink is deliberately much LIGHTER than the orange
 *  (relative luminance ~0.62 vs ~0.43) so the two differ in lightness, not
 *  only in hue — pinned by `iobChip.test.ts`. */
export const IOB_CHIP_COLORS: Record<IobChipKind, string> = {
  meal: "#f2a33a",
  correction: "#ffc2d6",
  long_acting: "#8b7cf6",
};

const LABELS: Record<IobChipKind, string> = {
  meal: "Meal",
  correction: "Correction",
  long_acting: "Long-acting",
};

/** At most three: the card's two boluses (oldest first, as the card draws
 *  them) and its long-acting dose. Empty when nothing is on board — the
 *  chip is then hidden. */
export function iobChipEntries(
  boluses: readonly BolusForRings[],
  basal: BasalContext | null,
  now: number,
): IobChipEntry[] {
  const out: IobChipEntry[] = [];
  for (const b of boluses.slice(0, 2)) {
    const fraction = remainingFraction(b.takenAt, now);
    if (fraction <= 0) continue;
    const kind: IobChipKind =
      b.color === CORRECTION_BOLUS_COLOR ? "correction" : "meal";
    out.push({
      key: b.id,
      kind,
      amount: `${b.units}u · ${coarseLeft(iobTimeRemainingMs([b], now))}`,
      label: LABELS[kind],
      fraction,
      color: IOB_CHIP_COLORS[kind],
    });
  }
  if (basal != null && !basal.due && basal.remainingFrac > 0) {
    out.push({
      key: "basal",
      kind: "long_acting",
      amount: `${basal.units}u · ${formatBasalHoursLeft(basal.hoursLeft, basal.due)}`,
      label: LABELS.long_acting,
      fraction: Math.max(0, Math.min(1, basal.remainingFrac)),
      color: IOB_CHIP_COLORS.long_acting,
    });
  }
  return out.slice(0, 3);
}
